import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const features = [
  {
    icon: 'ri-team-line',
    title: '多人实时协作',
    description: '支持最多50人同时在线编辑同一份文稿，所有修改实时同步，告别版本混乱和邮件来回。',
  },
  {
    icon: 'ri-history-line',
    title: '版本历史管理',
    description: '自动保存每一次修改记录，支持一键回溯任意历史版本，再也不用担心误删内容。',
  },
  {
    icon: 'ri-chat-check-line',
    title: '在线评论批注',
    description: '团队成员可以在文稿任意位置添加评论和批注，精准讨论每一处细节。',
  },
  {
    icon: 'ri-file-copy-line',
    title: '丰富文档模板',
    description: '数百种经过专业审核的模板库，涵盖合同、方案、报告等，一键套用快速启动。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '安全权限控制',
    description: '精细的文档权限管理，支持查看、评论、编辑、管理四级权限，保障文档安全。',
  },
  {
    icon: 'ri-cloud-line',
    title: '云端随时随地访问',
    description: '所有文稿存储在云端，电脑、平板、手机随时随地访问和编辑。',
  },
];

const steps = [
  { step: '01', icon: 'ri-user-add-line', title: '创建团队空间', description: '注册账号，创建团队空间，邀请成员加入' },
  { step: '02', icon: 'ri-file-add-line', title: '新建或选择模板', description: '从空白开始或使用丰富模板快速启动' },
  { step: '03', icon: 'ri-edit-line', title: '多人实时编辑', description: '团队成员同时在线协作，修改实时可见' },
  { step: '04', icon: 'ri-check-double-line', title: '审核与定稿', description: '在线评论批注，完成审核后一键导出定稿' },
];

const stats = [
  { value: '500,000+', label: '注册用户' },
  { value: '2,000,000+', label: '协作文档' },
  { value: '50,000+', label: '活跃团队' },
  { value: '99.9%', label: '服务可用率' },
];

const testimonials = [
  {
    quote: '一起草.com 彻底改变了我们团队的合同起草方式。以前一份合同要来回发十几封邮件，现在所有人同时在线上编辑，效率提升了好几倍。',
    author: '张伟',
    role: '某科技公司法务总监',
    avatar: '张',
  },
  {
    quote: '作为一家内容营销公司，我们每天都要产出大量文稿。一起草的协作功能让我们的编辑、设计、SEO团队能够无缝配合，内容产出效率提升了40%。',
    author: '李娜',
    role: '某内容营销公司CEO',
    avatar: '李',
  },
  {
    quote: '使用一起草.com 之后，我们团队的方案撰写流程变得非常顺畅。结构化的模板和实时协作让我们能在更短时间内产出更高质量的方案。',
    author: '王浩然',
    role: '某咨询公司项目经理',
    avatar: '王',
  },
];

const faqs = [
  {
    question: '一起草.com 支持多少人同时协作编辑一份文档？',
    answer: '一起草.com 支持最多50人同时在线编辑同一份文稿。所有成员的修改实时同步显示，系统以不同颜色标识不同成员的编辑内容，让协作过程清晰透明。对于更大规模的团队，我们提供企业版方案，支持更高并发数。',
  },
  {
    question: '如何保障文档的安全性？',
    answer: '我们采用多重安全措施保障您的文档安全。数据传输使用256位SSL加密，文档存储采用分布式加密存储。权限管理支持四级控制（查看、评论、编辑、管理），您可以精确控制每个成员的访问权限。同时，操作审计日志完整记录所有操作行为。',
  },
  {
    question: '支持哪些文档格式的导入导出？',
    answer: '一起草.com 支持导入 Word (.docx)、PDF、Markdown、纯文本等常见格式。导出支持 Word (.docx)、PDF、Markdown、HTML 等格式。导入导出过程中，文档格式和排版会得到最大程度的保留。',
  },
  {
    question: '免费版有哪些功能限制？',
    answer: '免费版支持最多5人团队协作、10个协作文档、1GB存储空间，包含核心的实时编辑、评论批注、版本管理等功能。付费版解锁更多团队成员、文档数量、存储空间和高级功能如模板定制、API接口等。',
  },
  {
    question: '可以在手机和平板上使用吗？',
    answer: '可以的。一起草.com 采用响应式设计，完美适配电脑、平板和手机等各种设备。您可以在任何设备上通过浏览器访问和编辑文档，所有内容实时同步。同时，我们即将推出移动端原生应用，提供更优化的移动体验。',
  },
  {
    question: '如何邀请团队成员加入协作？',
    answer: '您可以通过链接分享或邮箱邀请两种方式添加团队成员。创建文档后，点击右上角的"分享"按钮，生成协作链接发送给团队成员即可。您还可以为不同成员设置不同的协作权限，灵活控制每个人的操作范围。',
  },
];

const homepageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '一起草.com',
  url: 'https://szarssm.cn/',
  description: '专业在线文稿协作平台，支持多人实时共同起草文件、合同、方案与创意内容。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://szarssm.cn/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '一起草.com',
  url: 'https://szarssm.cn/',
  description: '专业在线文稿协作平台',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+86-400-XXX-XXXX',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <SEOMeta
        title="专业在线文稿协作平台"
        description="一起草.com 是您的专业在线文稿协作平台，支持多人实时共同起草文件、合同、方案与创意内容。无论团队协作还是个人创作，一起草.com 让每一份文稿从构思到定稿都更高效、更顺畅。"
        keywords="一起草.com,在线协作写作平台,多人共同起草文件,实时文稿协作工具,合同在线起草,团队文档协作,云端文稿编辑"
        canonicalUrl="https://szarssm.cn/"
        schema={{ ...homepageSchema, ...orgSchema }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20modern%20digital%20workspace%20with%20soft%20flowing%20geometric%20shapes%2C%20warm%20amber%20and%20cream%20gradients%2C%20minimalist%20design%2C%20clean%20composition%2C%20professional%20atmosphere%2C%20soft%20ambient%20glow%2C%20editorial%20quality%2C%208K%20resolution%2C%20balanced%20lighting%2C%20elegant%20tech%20aesthetic%20with%20warm%20tones&width=1920&height=1080&seq=hero-bg-yicao&orientation=landscape"
              alt="一起草.com 在线协作写作平台"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/25"></div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight">
              让每一份文稿
              <br />
              从构思到定稿都
              <span className="text-primary-300">更高效</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
              一起草.com 是您的专业在线文稿协作平台，支持多人实时共同起草文件、合同、方案与创意内容。无论团队协作还是个人创作，让协作写作变得前所未有的顺畅。
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-lg text-base font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                立即免费体验
              </Link>
              <Link
                to="/category/tuandui-xiezuo"
                className="px-8 py-3.5 rounded-lg text-base font-medium border border-white/30 text-white hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
            <div className="mt-14 flex items-center justify-center gap-8 text-white/60">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">500K+</div>
                <div className="text-xs md:text-sm mt-1">注册用户</div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">2M+</div>
                <div className="text-xs md:text-sm mt-1">协作文档</div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">50K+</div>
                <div className="text-xs md:text-sm mt-1">活跃团队</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                为什么选择一起草.com？
              </h2>
              <p className="mt-4 text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                我们深知文稿协作中的每一个痛点，用专业的产品能力为您解决从起草到定稿的每一个挑战。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group p-6 md:p-8 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-default"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-5 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                四步开始您的协作之旅
              </h2>
              <p className="mt-4 text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                从注册到完成第一份协作文稿，只需要简单的四个步骤。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step, index) => (
                <div key={index} className="relative text-center group">
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-12 left-[calc(50%+40px)] w-[calc(100%-80px)] h-px bg-background-300">
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary-400"></div>
                    </div>
                  )}
                  <div className="w-24 h-24 mx-auto flex items-center justify-center rounded-full bg-background-50 border-2 border-background-200 group-hover:border-primary-400 transition-colors mb-5">
                    <i className={`${step.icon} text-3xl text-primary-500`}></i>
                  </div>
                  <div className="text-xs font-bold text-primary-500 mb-2">{step.step}</div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 md:py-20 bg-primary-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-background-50">{stat.value}</div>
                  <div className="mt-2 text-sm text-primary-100">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                探索我们的内容分类
              </h2>
              <p className="mt-4 text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                按类别浏览专业文章，找到最适合您需求的协作方法和技巧。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group p-6 rounded-xl bg-background-100 hover:bg-accent-50 border border-transparent hover:border-accent-200 transition-all cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{cat.description}</p>
                  <div className="mt-3 text-xs text-accent-600 font-medium">
                    {cat.articleCount} 篇文章 <i className="ri-arrow-right-line ml-1"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-12 gap-4">
              <div>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                  最新文章
                </h2>
                <p className="mt-2 text-foreground-600">精选协作写作技巧与最佳实践</p>
              </div>
              <Link
                to="/category/tuandui-xiezuo"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.slice(0, 3).map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group rounded-xl overflow-hidden bg-background-50 hover:bg-background-200/50 transition-colors cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                      <span className="px-2 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">
                        {categories.find((c) => c.slug === article.category)?.name}
                      </span>
                      <span>{article.publishDate}</span>
                      <span>{article.readTime}</span>
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                用户怎么说
              </h2>
              <p className="mt-4 text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                来自真实用户的反馈，是我们不断前进的动力。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((item, index) => (
                <div key={index} className="p-6 md:p-8 rounded-xl bg-background-100">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                    &ldquo;{item.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-semibold text-sm">
                      {item.avatar}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{item.author}</div>
                      <div className="text-xs text-foreground-500">{item.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                常见问题
              </h2>
              <p className="mt-4 text-foreground-600">
                关于一起草.com，您可能想了解的问题
              </p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-background-50 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-lg text-foreground-500 transition-transform flex-shrink-0`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20geometric%20patterns%2C%20amber%20and%20cream%20tones%2C%20minimalist%20corporate%20design%2C%20clean%20subtle%20texture%2C%20professional%20elegant%20atmosphere%2C%20smooth%20flowing%20lines%2C%20warm%20inviting%20feel&width=1920&height=600&seq=cta-bg-yicao&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-600/90 to-primary-500/85"></div>

          <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50">
              准备好开始您的协作之旅了吗？
            </h2>
            <p className="mt-4 text-base text-primary-100 max-w-xl mx-auto leading-relaxed">
              免费注册一起草.com，体验全新的在线文稿协作方式。无需下载安装，打开浏览器即可开始。
            </p>
            <div className="mt-8">
              <Link
                to="/about"
                className="inline-flex px-8 py-3.5 rounded-lg text-base font-medium bg-background-50 text-primary-600 hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
              >
                立即免费体验
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}